"use client";

import { useEffect, useRef, useCallback } from "react";
import { driver, type DriveStep, type Config } from "driver.js";
import "driver.js/dist/driver.css";

const TOUR_STORAGE_KEY = "mhive-tour-completed";

export const useMhiveTour = () => {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
    if (driverRef.current) {
      driverRef.current.destroy();
    }
  }, []);

  const startTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }

    const driverObj = driver({
      ...driverConfig,
      onDestroyStarted: () => {
        completeTour();
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [completeTour]);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    startTour();
  }, [startTour]);

  useEffect(() => {
    // Check if user has completed the tour before
    const hasCompletedTour = localStorage.getItem(TOUR_STORAGE_KEY);

    if (!hasCompletedTour) {
      // Initialize the tour after a delay to ensure DOM is ready
      const timer = setTimeout(() => {
        startTour();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [startTour]);

  return {
    resetTour,
  };
};

const tourSteps: DriveStep[] = [
  {
    popover: {
      title: "Welcome to mHive! 🐝",
      description:
        "Your intelligent data orchestration platform. Let's take a quick tour to help you get started!",
    },
  },
  {
    element: ".mhive-logo",
    popover: {
      title: "mHive Logo",
      description:
        "Click here anytime to return to the main dashboard and overview of all your features.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: ".magic-bento-container",
    popover: {
      title: "Feature Dashboard",
      description: `
        <p class="mb-2">This is your central hub for all mHive features. Each card represents a powerful capability:</p>
        <ul class="list-disc list-inside space-y-1 text-sm">
          <li>Connect to APIs and data sources</li>
          <li>Analyze data with AI</li>
          <li>Manage access and security</li>
          <li>Synchronize across systems</li>
        </ul>
      `,
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="connections"]',
    popover: {
      title: "Connections 🔌",
      description:
        "Manage all your API connections and integrations in one place. Connect to external services, databases, and APIs seamlessly.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="data-spaces"]',
    popover: {
      title: "Data Spaces 🗂️",
      description:
        "Create virtual endpoints for your data. Data Spaces act as intelligent layers between your applications and data sources.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="access-control"]',
    popover: {
      title: "Access Control 🔒",
      description:
        "Control who can access your data and features. Set up permissions, roles, and security policies for your organization.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="data-analyst"]',
    popover: {
      title: "Data Review 📊",
      description:
        "Test and confirm your data. Inspect schemas, validate endpoints, and verify data integrity across your connected sources.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="documents"]',
    popover: {
      title: "Documents 📚",
      description:
        "Store and manage your organization's documents, policies, and knowledge base. Make information easily accessible to your team.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="mcore"]',
    popover: {
      title: "mCore 💡",
      description:
        "The source of your organization's identity and intelligence. mCore stores organizational context, patterns, and insights that power all other features.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-card="sync-management"]',
    popover: {
      title: "Sync Management 🔄",
      description:
        "Synchronize data across your connected systems. Keep everything in sync automatically with intelligent data flows.",
      side: "top",
      align: "center",
    },
  },
  {
    element: ".mhive-glow-bar",
    popover: {
      title: "Universal Memory Chat 💬",
      description:
        "Click this glow bar to open your AI assistant. It has access to all your connected data and can help you with queries, analysis, and workflows across your entire system.",
      side: "top",
      align: "center",
    },
  },
  {
    element: ".theme-toggle",
    popover: {
      title: "Theme Toggle 🌓",
      description:
        "Switch between light and dark modes to suit your preference.",
      side: "bottom",
      align: "end",
    },
  },
  {
    popover: {
      title: "You're All Set! 🎉",
      description: `
        <p class="mb-2">You're ready to start exploring mHive. Here are some suggestions:</p>
        <ol class="list-decimal list-inside space-y-1 text-sm mb-3">
          <li>Connect your first API or data source</li>
          <li>Set up your organization profile in mCore</li>
          <li>Try asking the AI assistant a question</li>
        </ol>
        <p class="text-xs text-gray-500">You can restart this tour anytime from the help menu.</p>
      `,
    },
  },
];

const driverConfig: Config = {
  showProgress: true,
  showButtons: ["next", "previous", "close"],
  progressText: "{{current}} of {{total}}",
  nextBtnText: "Next",
  prevBtnText: "Back",
  doneBtnText: "Finish",
  popoverClass: "mhive-tour-popover",
  steps: tourSteps,
};
